/**
 * Agency Portal v3 (2026-10-10) — map raw API `server_reason` codes
 * to friendly English copy. One source of truth for every agency
 * surface so the portal never leaks `reserved_brand:400`,
 * `white_label_feature_disabled:403`, or `server_error:502` to a
 * non-technical agency owner.
 *
 * This supplements the per-surface `humanize*` helpers (which know
 * about branch-specific wording like "Scan N domains"). This one
 * is the fallback for load-time errors + the "catch-all" branch
 * in action handlers.
 *
 * Reads the full `ApiError` so it can specialise on `kind` + the
 * `server_reason` string. Falls back to a neutral sentence so the
 * UI never shows a code.
 */

import { ApiError } from '../api/http-client.js';

/** Map a raw code (or ApiError) to friendly copy. Never returns a
 *  code; worst case returns the generic "something went wrong" copy. */
export function humanizeAgencyError(err: unknown): string {
  // Explicit code string from a typed API wrapper branch.
  if (typeof err === 'string') return mapReason(err) ?? defaultCopy();
  if (err instanceof ApiError) {
    const specific = err.server_reason !== undefined ? mapReason(err.server_reason) : null;
    if (specific !== null) return specific;
    if (err.kind === 'rate_limited') {
      return 'Too many requests. Pause briefly and try again.';
    }
    if (err.kind === 'forbidden') {
      return 'You do not have permission to do this.';
    }
    if (err.kind === 'not_found') {
      return 'We could not find that.';
    }
    if (err.kind === 'bad_request') {
      return 'That input was not accepted. Check the fields and try again.';
    }
    if (err.kind === 'network_timeout') {
      return 'This may have saved — refresh to check before trying again.';
    }
    if (err.kind === 'network_error') {
      return 'Could not reach Sill. Check your connection and try again.';
    }
    return defaultCopy();
  }
  if (err instanceof Error) {
    const specific = mapReason(err.message);
    if (specific !== null) return specific;
    return defaultCopy();
  }
  return defaultCopy();
}

function defaultCopy(): string {
  return 'Something went wrong. Try again, or contact Sill if it keeps failing.';
}

/** Closed set of known reason codes → friendly copy. Returns `null`
 *  when the input is not a known code (callers fall back to the
 *  generic copy, never to the raw string). */
export function mapReason(reason: string): string | null {
  switch (reason) {
    // White label.
    case 'reserved_brand':
      return 'This name is too close to a protected brand (Sill, Google, Anthropic, …). Use your agency\'s own name.';
    case 'invalid_display_name':
      return 'Agency name must be 1–80 characters, with no control characters.';
    case 'invalid_logo_url':
      return 'Logo URL must start with https:// and be under 2048 characters.';
    case 'invalid_accent_hex':
      return 'Accent must be a 6-digit hex colour, like #2f6feb.';
    case 'invalid_contact_url':
      return 'Contact link must be an https:// URL or mailto:you@example.com.';
    case 'invalid_cta_mode':
      return 'Choose one of: sign-up button, your own link, or no button.';
    case 'white_label_feature_disabled':
      return 'White label is an add-on. Contact Sill to enable it.';

    // Scan widget.
    case 'agency_feature_disabled':
      return 'Agency features are not enabled on this account. Contact Sill.';
    case 'invalid_origin':
      return 'That website address is not valid. Use an https:// URL.';
    case 'duplicate_origin':
      return 'That website is already in the list.';
    case 'too_many_origins':
      return 'Up to 10 websites are allowed per agency.';

    // Reports.
    case 'duplicate_domain':
      return 'One of these domains is already in another import. Try again in a minute.';
    case 'over_batch_limit':
      return 'That is over the 50-per-import limit. Split your list.';
    case 'too_many_domains':
      return 'Only 50 domains per import. Split your list and try again.';
    case 'consent_required':
      return 'Tick the consent box to send.';
    case 'invalid_email':
      return 'Check the email address.';
    case 'invalid_subject':
      return 'Keep the subject short, drop any URLs, and avoid all caps.';
    case 'invalid_first_name':
      return 'First name is letters, spaces, hyphens or apostrophes only.';
    case 'row_not_ready':
      return 'This scan has not finished. Try again when the status is Ready.';
    case 'blocked':
      return 'Sill cannot email this address (it unsubscribed or bounced earlier). Share the report link instead.';
    case 'send_failed':
      return 'The email was not sent. Try again in a few minutes, or share the link.';
    case 'empty':
      return 'Add at least one domain.';

    // ADR-0263 §7.5 as amended for security (2026-11-03) — simple
    // fallbacks for the new 429 reasons. The caller passes the full
    // ApiError for richer messages via `humanizeAgency429`
    // (defined below) when the dimension data is available.
    case 'email_limit_reached':
      return 'You have already sent 2 to this recipient this month.';
    case 'monthly_scan_limit_reached':
      return 'This month\'s scan pool is used up. Resets on the 1st.';

    // Referral.
    case 'invalid_shape':
      return 'Use 3–32 characters: lowercase letters, numbers, dashes or underscores.';
    case 'slug_reserved':
      return 'That link name is reserved. Try another.';
    case 'slug_taken':
      return 'That link name is already in use by another agency.';

    // Shared.
    case 'role_insufficient':
      return 'Only owners and admins can do this.';
    case 'acting_session_denied':
      return 'Return to your home account to do this.';
    case 'rate_limited':
      return 'Too many requests. Pause briefly and try again.';
    case 'bad_origin':
      return 'This browser blocked the request. Try again from the Sill dashboard tab.';
    case 'invalid_body':
      return 'That input was not accepted. Check the fields and try again.';
    case 'not_set':
    case 'not_found':
      return 'We could not find that.';
    case 'forbidden':
      return 'You do not have permission to do this.';

    default:
      return null;
  }
}
